import * as constants from "@/config/constants";
import { identity, isEmpty, pathOr, pipe } from "ramda";
import { createSelector } from "reselect";

import {
	getAllFiltering,
	getCompanyPositionOverride,
	getContentfulCompanies,
	getCreditQuality,
	getFilter,
	getFundingType,
	getGenderFromFilteringState,
	getIsMobileTargetedPath,
	getIsTargetedPath,
	getLoanAmount,
	getLoanAmountServiced,
	getMediaAlphaMinimumBid,
	getMonthlyRevenue,
	getMortgageType,
	getPolicyTypeFromFilteringState,
	getSortCompaniesByMediaAlphaBid,
	getStateForSentryExceptions,
	getUserGeo,
	getVertical,
	isModalOrFilteringUiFilteredSelector,
} from ".";
import { customBehaviorByPathsWithFilter } from "../../config/customBehaviorByPath";
import { sortCompanies } from "../helpers";
import {
	filterAdNetworkCompanies,
	filterByCreditQuality,
	filterByFundingType,
	filterByHairGrowthType,
	filterByLoanAmount,
	filterByLoanAmountServiced,
	filterByMonthlyRevenue,
	filterByMortgageType,
	filterByPolicyType,
	filterEligibleAndGeoRelevantCompanies,
} from "../helpers/filters";
import { logError } from "../helpers/logError";
import {
	getIsMediaAlphaTargetedPath,
	getMediaAlphaCompanies,
	processMediaAlphaCompanies,
} from "./mediaAlpha";

const getUnfilteredCompanies = createSelector(
	[getContentfulCompanies, getMediaAlphaCompanies, getMediaAlphaMinimumBid],
	(contentfulCompanies, mediaAlphaCompanies, mediaAlphaMinimumBid) => {
		if (mediaAlphaCompanies) {
			return processMediaAlphaCompanies(
				contentfulCompanies,
				mediaAlphaCompanies,
				mediaAlphaMinimumBid,
			);
		}

		return filterAdNetworkCompanies(contentfulCompanies);
	},
);

// has visibility for testing purposes
export const companiesSelector = createSelector(
	[
		getUnfilteredCompanies,
		getContentfulCompanies,
		isModalOrFilteringUiFilteredSelector,
		getLoanAmount,
		getCreditQuality,
		getFundingType,
		getPolicyTypeFromFilteringState,
		getMortgageType,
		getMonthlyRevenue,
		getGenderFromFilteringState,
		getUserGeo,
		getIsTargetedPath,
		getVertical,
		getFilter,
		getIsMediaAlphaTargetedPath,
		getStateForSentryExceptions,
		getLoanAmountServiced,
	],
	(
		unfilteredCompanies,
		contentfulCompanies,
		isModalOrFilteringUiFiltered,
		loanAmount,
		creditQuality,
		fundingType,
		policyType,
		mortgageType,
		monthlyRevenue,
		gender,
		userGeo,
		isTargetedPath,
		vertical,
		filter,
		isMediaAlphaTargetedPath,
		sentryExceptionsState,
		loanAmountServiced,
	) => {
		// apply universal filters (targeted, geo, legacy media alpha)
		const defaultFilteredCompanies = filterEligibleAndGeoRelevantCompanies(
			unfilteredCompanies,
			userGeo,
			isMediaAlphaTargetedPath,
		);
		const companies = isTargetedPath
			? defaultFilteredCompanies
			: unfilteredCompanies;

		const applyFilters = (...funcs) => pipe(...funcs)(companies);
		const userFilteredCompanies = applyFilters(
			...[
				filterByCreditQuality(creditQuality),
				filterByLoanAmount(loanAmount),
				filterByFundingType(fundingType),
				filterByMonthlyRevenue(monthlyRevenue),
				filterByPolicyType(policyType),
				filterByMortgageType(mortgageType),
				filterByHairGrowthType(gender),
				filterByLoanAmountServiced(loanAmountServiced),
			],
		);

		const pathFilteredCompanies = applyFilters(
			...pathOr(
				[identity],
				[constants.SITE, vertical, filter, constants.CUSTOM_FILTER],
				customBehaviorByPathsWithFilter,
			),
		);

		// for the future: apply filtering more uniformly rather than distinguishing between modal and non-modal filtering
		const companiesToRender = isModalOrFilteringUiFiltered
			? userFilteredCompanies
			: pathFilteredCompanies;
		const shouldRenderFallbackCompanies = isEmpty(companiesToRender);
		if (shouldRenderFallbackCompanies) {
			return getFallbackCompanies(
				contentfulCompanies,
				vertical,
				sentryExceptionsState,
			);
		} else return companiesToRender;
	},
);

export const getFallbackCompanies = (
	contentfulCompanies,
	vertical,
	sentryExceptionState,
) => {
	if (!vertical)
		throw new Error(
			"vertical not passed to ensureNonZeroCompanyCount helper function",
		);
	if (!contentfulCompanies)
		throw new Error(
			`No Contentful companies associated with vertical: ${vertical}. Every vertical needs at least 1 company`,
		);

	const fallbackCompaniesFilter = pipe(
		filterEligibleAndGeoRelevantCompanies, // company must be recommended and permitted in user's geo
		filterAdNetworkCompanies, // filter out unmatched ad network companies
	);
	const fallbackCompanies = fallbackCompaniesFilter(contentfulCompanies);
	if (fallbackCompanies.length === 0) {
		throw new Error(
			`Unable to render fallback companies. No eligible fallback companies for vertical: ${vertical}. There must be a recommended company with an organic partnership for every vertical.`,
		);
	} else {
		logError(
			`Fallback companies rendered for vertical: ${vertical}. This may have occurred for various reasons (user applied filtering, no MA company matches in payload, etc...)`,
		);
		return fallbackCompanies;
	}
};
export const sortedCompaniesSelector = createSelector(
	[
		getVertical,
		getFilter,
		getAllFiltering,
		companiesSelector,
		getIsMobileTargetedPath,
		getSortCompaniesByMediaAlphaBid,
		getCompanyPositionOverride,
	],
	(
		vertical,
		filter,
		filteringInformation,
		companies,
		isMobileTargetedPath,
		sortCompaniesByMediaAlphaBid,
		companyPositionOverride,
	) =>
		sortCompanies(
			vertical,
			filter,
			filteringInformation,
			companies,
			isMobileTargetedPath,
			sortCompaniesByMediaAlphaBid,
			companyPositionOverride,
		),
);
